import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Alert
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import PendingChargesList from './PendingChargesList';
const { rootUrl } = require('../../../config/config');

const ChargeApplication = ({ remittanceProcessingId, plateNo, weekKey, onUpdate }) => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [selectedCharges, setSelectedCharges] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const handleApplyCharges = async () => {
        setError(null);
        setSuccess(null);

        if (selectedCharges.length === 0) {
            setError('Please select at least one charge to apply');
            return;
        }

        // Calculate totals for validation
        const totalInvoices = selectedCharges
            .filter(c => c.charge_type === 'JOB_CARD')
            .reduce((sum, c) => sum + parseFloat(c.charge_amount || 0), 0);

        const totalExpenses = selectedCharges
            .filter(c => c.charge_type === 'EXPENSE')
            .reduce((sum, c) => sum + parseFloat(c.charge_amount || 0), 0);

        if (totalInvoices > 0 && totalInvoices === totalExpenses) {
            setError(`Error: Total of Invoices (${formatCurrency(totalInvoices)}) cannot be the same as Total of Expenses (${formatCurrency(totalExpenses)}).`);
            return;
        }

        if (!window.confirm(`Apply ${selectedCharges.length} charge(s) to remittance?`)) {
            return;
        }

        setLoading(true);
        try {
            const chargeIds = selectedCharges.map(charge => ({
                charge_type: charge.charge_type,
                charge_reference_id: charge.charge_reference_id
            }));

            const response = await axios.post(
                `${rootUrl}/api/remittance/apply-charges`,
                {
                    remittance_processing_id: remittanceProcessingId,
                    charge_ids: chargeIds,
                    applied_by: localStorage.getItem('username') || 'user'
                },
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data && response.data.success) {
                setSuccess('Charges applied successfully!');
                setSelectedCharges([]);
                onUpdate && onUpdate(response.data.data);
                // Clear success after 3 seconds
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(response.data?.message || 'Failed to apply charges');
            }
        } catch (error) {
            console.error('Error applying charges:', error);
            setError(error.response?.data?.message || 'Failed to apply charges');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '0.00';
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(amount);
    };

    const totalSelected = selectedCharges.reduce((sum, charge) => {
        return sum + parseFloat(charge.charge_amount || 0);
    }, 0);

    return (
        <div className="space-y-4">
            {error && (
                <Alert
                    color="red"
                    icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {success && (
                <Alert
                    color="green"
                    icon={<FontAwesomeIcon icon={faCheckCircle} />}
                    onClose={() => setSuccess(null)}
                >
                    {success}
                </Alert>
            )}

            <Card>
                <CardBody>
                    <Typography variant="h6" className="mb-4">
                        Select Charges to Apply
                    </Typography>
                    <PendingChargesList
                        plateNo={plateNo}
                        weekKey={weekKey}
                        onSelectionChange={setSelectedCharges}
                    />
                </CardBody>
            </Card>

            {selectedCharges.length > 0 && (
                <Card className="bg-blue-50">
                    <CardBody>
                        <div className="flex justify-between items-center">
                            <div>
                                <Typography variant="small" color="gray">
                                    Selected Charges: {selectedCharges.length}
                                </Typography>
                                <Typography variant="h6" className="text-blue-600">
                                    Total: {formatCurrency(totalSelected)}
                                </Typography>
                            </div>
                            <Button
                                color="green"
                                onClick={handleApplyCharges}
                                disabled={loading}
                            >
                                <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                                {loading ? 'Applying...' : 'Apply Charges'}
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            )}
        </div>
    );
};

export default ChargeApplication;

